export { DYNAMIC_ABBY_CONFIG_KEYS, defineConfig } from './chunk-VRPVF57Z.mjs';
import { parseCookies } from './chunk-KNI6RYY6.mjs';
import { getVariantWithHeighestWeightOrFirst, getWeightedRandomVariant } from './chunk-5AXMGLYW.mjs';
import './chunk-ZXBM3B6U.mjs';
import './chunk-Z3D35RMJ.mjs';
export { abbyConfigSchema, abbyEventSchema, remoteConfigValue, remoteConfigValueStringSchema } from './chunk-UO4INTS4.mjs';
export { AbbyEventType } from './chunk-OEWW7U5G.mjs';
import { remoteConfigStringToType, getDefaultRemoteConfigValue, stringifyRemoteConfigValue } from './chunk-HK6BSHXQ.mjs';
export { assertUnreachable, getABStorageKey, getDefaultRemoteConfigValue, getFFStorageKey, getRCStorageKey, getUseFeatureFlagRegex, remoteConfigStringToType, stringifyRemoteConfigValue } from './chunk-HK6BSHXQ.mjs';
import { HttpService } from './chunk-YETZGIZF.mjs';
export { HttpService } from './chunk-YETZGIZF.mjs';
import { DEFAULT_FEATURE_FLAG_VALUE, ABBY_WINDOW_KEY, ABBY_AB_STORAGE_PREFIX, ABBY_FF_STORAGE_PREFIX, ABBY_RC_STORAGE_PREFIX } from './chunk-2HPJHXZH.mjs';
export { ABBY_AB_STORAGE_PREFIX, ABBY_BASE_URL, ABBY_FF_STORAGE_PREFIX, ABBY_INSTANCE_KEY, ABBY_RC_STORAGE_PREFIX, ABBY_WINDOW_KEY, DEFAULT_FEATURE_FLAG_VALUE, DOCS_URL } from './chunk-2HPJHXZH.mjs';
import { __privateAdd, __privateGet, __async, __spreadProps, __spreadValues, __privateSet } from './chunk-XEH7ODH4.mjs';

// src/index.ts
var _data;
var Abby = class {
  constructor(config, persistantTestStorage, persistantFlagStorage, persistentRemoteConfigStorage) {
    this.config = config;
    this.persistantTestStorage = persistantTestStorage;
    this.persistantFlagStorage = persistantFlagStorage;
    this.persistentRemoteConfigStorage = persistentRemoteConfigStorage;
    this.log = (...args) => this.config.debug ? console.log(`core.Abby`, ...args) : () => {
    };
    __privateAdd(this, _data, {
      tests: {},
      flags: {},
      remoteConfig: {}
    });
    this.listeners = /* @__PURE__ */ new Set();
    this.dataInitialized = false;
    this.flagOverrides = /* @__PURE__ */ new Map();
    this.testOverrides = /* @__PURE__ */ new Map();
    this.remoteConfigOverrides = /* @__PURE__ */ new Map();
    this.COOKIE_CONSENT_KEY = "$_abcc_$";
    var _a, _b, _c;
    this._cfg = config;
    __privateGet(this, _data).flags = Object.values((_a = this._cfg.flags) != null ? _a : {}).reduce(
      (acc, flagName) => {
        acc[flagName] = DEFAULT_FEATURE_FLAG_VALUE;
        return acc;
      },
      {}
    );
    __privateGet(this, _data).tests = (_b = config.tests) != null ? _b : {};
    __privateGet(this, _data).remoteConfig = Object.keys((_c = config.remoteConfig) != null ? _c : {}).reduce(
      (acc, remoteConfigName) => {
        acc[remoteConfigName] = this.getDefaultRemoteConfigValue(
          remoteConfigName,
          config.remoteConfig
        );
        return acc;
      },
      {}
    );
    if (persistantTestStorage) {
      this.persistantTestStorage = {
        get: (...args) => persistantTestStorage.get(...args),
        set: (...args) => {
          var _a2, _b2;
          if ((_a2 = config.cookies) == null ? void 0 : _a2.disableByDefault)
            return;
          const [key, value] = args;
          persistantTestStorage.set(key, value, { expiresInDays: (_b2 = config.cookies) == null ? void 0 : _b2.expiresInDays });
        }
      };
    }
  }
  /**
   * Helper function to load the projects data from the Abby API
   * and init the local data
   */
  loadProjectData() {
    return __async(this, null, function* () {
      this.log(`loadProjectData()`);
      const start = Date.now();
      if (typeof window !== "undefined" && ABBY_WINDOW_KEY in window && window[ABBY_WINDOW_KEY] != null) {
        this.log(`loadProjectData() => using window data`);
        return this.init(window[ABBY_WINDOW_KEY]);
      }
      const data = yield HttpService.getProjectData({
        projectId: this.config.projectId,
        environment: this.config.currentEnvironment,
        url: this.config.apiUrl,
        fetch: this._cfg.fetch,
        __experimentalCdnUrl: this._cfg.__experimentalCdnUrl ? `${this._cfg.__experimentalCdnUrl}/${this.config.projectId}/${this.config.currentEnvironment}` : void 0
      });
      if (!data) {
        this.log(`loadProjectData() => no data`);
        return;
      }
      this.log(`loadProjectData() => finished. Elapsed: `, Date.now() - start);
      return this.init(data);
    });
  }
  getProjectDataAsync() {
    return __async(this, null, function* () {
      this.log(`getProjectDataAsync()`);
      if (!this.dataInitialized) {
        yield this.loadProjectData();
        this.dataInitialized = true;
      }
      return this.getProjectData();
    });
  }
  /**
   * Helper function to transform the data which is fetched from the server
   * to the local data structure
   */
  responseToLocalData(data) {
    var _a, _b;
    return {
      tests: data.tests.reduce(
        (acc, { name, weights }) => {
          if (!acc[name]) {
            return acc;
          }
          acc[name] = __spreadProps(__spreadValues({}, acc[name]), {
            weights
          });
          return acc;
        },
        (_a = this.config.tests) != null ? _a : {}
      ),
      flags: data.flags.reduce(
        (acc, { name, value }) => {
          acc[name] = value;
          return acc;
        },
        {}
      ),
      remoteConfig: ((_b = data.remoteConfig) != null ? _b : []).reduce(
        (acc, { name, value }) => {
          acc[name] = value;
          return acc;
        },
        {}
      )
    };
  }
  /**
   * Function to get the locally stored information about A/B tests, feature flags and remote config
   * This also includes the overrides from the dev tools and the local overrides
   * @returns the local data
   */
  getProjectData() {
    this.log(`getProjectData()`);
    return {
      tests: Object.entries(__privateGet(this, _data).tests).reduce((acc, [testName, test]) => {
        acc[testName] = __spreadProps(__spreadValues({}, test), {
          selectedVariant: this.getTestVariant(testName)
        });
        return acc;
      }, __privateGet(this, _data).tests),
      flags: Object.keys(__privateGet(this, _data).flags).reduce((acc, flagName) => {
        acc[flagName] = this.getFeatureFlag(flagName);
        return acc;
      }, __privateGet(this, _data).flags),
      remoteConfig: Object.keys(__privateGet(this, _data).remoteConfig).reduce((acc, remoteConfigName) => {
        acc[remoteConfigName] = this.getRemoteConfig(
          remoteConfigName
        );
        return acc;
      }, __privateGet(this, _data).remoteConfig)
    };
  }
  /**
   * Function to init the local data with the data from the server as well as
   * potentially setting the local overrides which are read from document.cookie if
   * we are in a browser environment
   * @param data
   * @returns
   */
  init(data) {
    this.log(`init()`, data);
    __privateSet(this, _data, this.responseToLocalData(data));
    this.notifyListeners();
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      this.setLocalOverrides(document.cookie);
    }
    return this.getProjectData();
  }
  /**
   * Function to get the value of a feature flag. This includes
   * the overrides from the dev tools and the local overrides if in development mode
   * otherwise it will return the value retrieved from the server
   * @param key the name of the feature flag
   * @returns the value of the feature flag
   */
  getFeatureFlag(key) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
    this.log(`getFeatureFlag()`, key);
    const storedValue = __privateGet(this, _data).flags[key];
    const localOverride = (_a = this.flagOverrides) == null ? void 0 : _a.get(key);
    if (localOverride != null) {
      return localOverride;
    }
    if (process.env.NODE_ENV === "development") {
      const devOverride = (_d = (_c = (_b = this.config.settings) == null ? void 0 : _b.flags) == null ? void 0 : _c.devOverrides) == null ? void 0 : _d[key];
      if (devOverride != null) {
        return devOverride;
      }
    }
    const defaultValue = (_f = (_e = this._cfg.settings) == null ? void 0 : _e.flags) == null ? void 0 : _f.defaultValue;
    if (storedValue !== void 0) {
      this.log(`getFeatureFlag() => storedValue:`, storedValue);
      return storedValue;
    }
    const hasFallbackValue = key in ((_i = (_h = (_g = this._cfg.settings) == null ? void 0 : _g.flags) == null ? void 0 : _h.fallbackValues) != null ? _i : {});
    if (hasFallbackValue) {
      const fallbackValue = (_l = (_k = (_j = this._cfg.settings) == null ? void 0 : _j.flags) == null ? void 0 : _k.fallbackValues) == null ? void 0 : _l[key];
      if (fallbackValue !== void 0) {
        if (typeof fallbackValue === "boolean") {
          this.log(`getFeatureFlag() => fallbackValue:`, fallbackValue);
          return fallbackValue;
        } else {
          const envFallbackValue = fallbackValue[this._cfg.currentEnvironment];
          if (envFallbackValue !== void 0) {
            this.log(`getFeatureFlag() => envFallbackValue:`, envFallbackValue);
            return envFallbackValue;
          }
        }
      }
    }
    this.log(`getFeatureFlag() => defaultValue:`, defaultValue != null ? defaultValue : false);
    return defaultValue != null ? defaultValue : false;
  }
  /**
   * Function to get the value of a remote config. This includes
   * the overrides from the dev tools and the local overrides if in development mode
   * otherwise it will return the value retrieved from the server
   * @param key the name of the remote config
   * @returns the value of the remote config
   */
  getRemoteConfig(key) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
    this.log(`getRemoteConfig()`, key);
    const storedValue = __privateGet(this, _data).remoteConfig[key];
    const localOverride = (_a = this.remoteConfigOverrides) == null ? void 0 : _a.get(key);
    if (localOverride !== void 0) {
      return localOverride;
    }
    if (process.env.NODE_ENV === "development") {
      const devOverride = (_d = (_c = (_b = this.config.settings) == null ? void 0 : _b.remoteConfig) == null ? void 0 : _c.devOverrides) == null ? void 0 : _d[key];
      if (devOverride != null) {
        return devOverride;
      }
    }
    const defaultValue = (_h = (_f = (_e = this._cfg.settings) == null ? void 0 : _e.remoteConfig) == null ? void 0 : _f.defaultValues) == null ? void 0 : _h[(_g = this._cfg.remoteConfig) == null ? void 0 : _g[key]];
    if (storedValue === void 0) {
      const fallbackValue = key in ((_k = (_j = (_i = this._cfg.settings) == null ? void 0 : _i.remoteConfig) == null ? void 0 : _j.fallbackValues) != null ? _k : {});
      if (fallbackValue) {
        return (_n = (_m = (_l = this._cfg.settings) == null ? void 0 : _l.remoteConfig) == null ? void 0 : _m.fallbackValues) == null ? void 0 : _n[key];
      }
      if (defaultValue != null) {
        return defaultValue;
      }
    }
    this.log(`getRemoteConfig() => storedValue:`, storedValue);
    return storedValue;
  }
  /**
   * Function to get the value of a test variant. This includes
   * the overrides from the dev tools and the local overrides if in development mode
   * if there is no override or a stored variant we generate a new one and store it
   * @param key The name of the test
   * @returns the value of the test variant
   */
  getTestVariant(key) {
    var _a, _b, _c;
    this.log(`getTestVariant()`, key);
    const { variants, weights } = __privateGet(this, _data).tests[key];
    const override = this.testOverrides.get(key);
    if (process.env.NODE_ENV === "development" && override != null) {
      return override;
    }
    const persistedValue = (_a = this.persistantTestStorage) == null ? void 0 : _a.get(key);
    if (persistedValue != null) {
      this.log(`getTestVariant() => persistedValue:`, persistedValue);
      return persistedValue;
    }
    if ((_b = this.config.cookies) == null ? void 0 : _b.disableByDefault) {
      return getVariantWithHeighestWeightOrFirst(variants, weights);
    }
    const weightedVariant = getWeightedRandomVariant(variants, weights);
    (_c = this.persistantTestStorage) == null ? void 0 : _c.set(key, weightedVariant);
    this.log(`getTestVariant() => weightedVariant:`, weightedVariant);
    return weightedVariant;
  }
  /**
   * Helper function update the locally stored variant for a test
   * @param key the name of the test
   * @param override the value to override the test variant with
   */
  updateLocalVariant(key, override) {
    var _a;
    this.testOverrides.set(key, override);
    (_a = this.persistantTestStorage) == null ? void 0 : _a.set(key, override);
    this.notifyListeners();
  }
  /**
   * Helper function to update the locally stored value of a feature flag
   * @param name the name of the feature flag
   * @param value the value to override the feature flag with
   */
  updateFlag(name, value) {
    var _a;
    this.flagOverrides.set(name, value);
    if (process.env.NODE_ENV === "development") {
      (_a = this.persistantFlagStorage) == null ? void 0 : _a.set(name, this.stringifiedValue(value));
    }
    this.notifyListeners();
  }
  /**
   * Helper function to update the locally stored value of a remote config
   * @param name the name of the remote config
   * @param value the value to override the remote config with
   */
  updateRemoteConfig(name, value) {
    var _a;
    this.remoteConfigOverrides.set(name, value);
    if (process.env.NODE_ENV === "development") {
      (_a = this.persistentRemoteConfigStorage) == null ? void 0 : _a.set(name, this.stringifiedValue(value));
    }
    this.notifyListeners();
  }
  /**
   * Function to subscribe to changes in the local data
   * @param listener the function to call when the local data changes
   * @returns the unsubscribe function
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  /**
   * Function to notify all listeners that the local data has changed
   */
  notifyListeners() {
    this.listeners.forEach((listener) => listener(this.getProjectData()));
  }
  /**
   * Function to get the config passed to the Abby class
   */
  getConfig() {
    return this.config;
  }
  /**
   * Function to get all variants for a test
   */
  getVariants(testName) {
    return __privateGet(this, _data).tests[testName].variants;
  }
  /**
   * Function to initially set the local overrides from the cookies
   * this can be either a cookie from `document.cookie` or a cookie header.
   * This should be called after the init function
   * @param cookies the cookie string
   */
  setLocalOverrides(cookies) {
    const parsedCookies = parseCookies(cookies);
    Object.entries(parsedCookies).forEach(([cookieName, cookieValue]) => {
      var _a, _b, _c;
      if (!cookieName.includes(`${this.config.projectId}_`)) {
        return;
      }
      if (cookieName.startsWith(ABBY_AB_STORAGE_PREFIX)) {
        const testName = cookieName.replace(
          `${ABBY_AB_STORAGE_PREFIX}${this.config.projectId}_`,
          ""
        );
        if (testName === this.COOKIE_CONSENT_KEY) {
          (_b = (_a = this.config).cookies) != null ? _b : _a.cookies = {};
          this.config.cookies.disableByDefault = cookieValue !== "true";
          return;
        }
        this.testOverrides.set(testName, cookieValue);
        (_c = this.persistantTestStorage) == null ? void 0 : _c.set(testName, cookieValue);
      }
      if (cookieName.startsWith(ABBY_FF_STORAGE_PREFIX)) {
        const flagName = cookieName.replace(
          `${ABBY_FF_STORAGE_PREFIX}${this.config.projectId}_`,
          ""
        );
        const flagValue = cookieValue === "true";
        this.flagOverrides.set(flagName, flagValue);
      }
      if (cookieName.startsWith(ABBY_RC_STORAGE_PREFIX) && this._cfg.remoteConfig) {
        const remoteConfigName = cookieName.replace(
          `${ABBY_RC_STORAGE_PREFIX}${this.config.projectId}_`,
          ""
        );
        const remoteConfigValue2 = remoteConfigStringToType({
          remoteConfigType: this._cfg.remoteConfig[remoteConfigName],
          stringifiedValue: cookieValue
        });
        this.remoteConfigOverrides.set(remoteConfigName, remoteConfigValue2);
      }
    });
  }
  getDefaultRemoteConfigValue(remoteConfigName, remoteConfig) {
    var _a, _b, _c;
    const remoteConfigType = remoteConfig[remoteConfigName];
    const defaultValue = (_c = (_b = (_a = this.config.settings) == null ? void 0 : _a.remoteConfig) == null ? void 0 : _b.defaultValues) == null ? void 0 : _c[remoteConfigType];
    if (defaultValue !== void 0) {
      return defaultValue;
    }
    return getDefaultRemoteConfigValue(remoteConfigType);
  }
  stringifiedValue(value) {
    if (typeof value === "boolean") {
      return value.toString();
    }
    return stringifyRemoteConfigValue(value);
  }
  /**
   * Retruns an Array of all flags with their name and value
   */
  getFeatureFlags() {
    return Object.keys(__privateGet(this, _data).flags).map((flagName) => ({
      name: flagName,
      value: this.getFeatureFlag(flagName)
    }));
  }
  /**
   * Retruns an Array of all remote config variables with their name and value
   */
  getRemoteConfigVariables() {
    return Object.keys(__privateGet(this, _data).remoteConfig).map((configName) => ({
      name: configName,
      value: this.getRemoteConfig(configName)
    }));
  }
  /**
   * Enables the usage of cookies for the storage of user data
   * and also sets the cookies if possible
   */
  enableCookies() {
    var _a, _b, _c;
    (_b = (_a = this.config).cookies) != null ? _b : _a.cookies = {};
    this.config.cookies.disableByDefault = false;
    (_c = this.persistantTestStorage) == null ? void 0 : _c.set(this.COOKIE_CONSENT_KEY, "true");
    Object.keys(__privateGet(this, _data).tests).forEach((testName) => {
      var _a2;
      (_a2 = this.persistantTestStorage) == null ? void 0 : _a2.set(testName, this.getTestVariant(testName));
    });
  }
  /**
   * Disables the usage of cookies for storage of user data
   * and also removes all set cookies if possible
   */
  disableCookies() {
    var _a, _b, _c;
    (_b = (_a = this.config).cookies) != null ? _b : _a.cookies = {};
    this.config.cookies.disableByDefault = true;
    (_c = this.persistantTestStorage) == null ? void 0 : _c.set(this.COOKIE_CONSENT_KEY, "false");
    Object.keys(__privateGet(this, _data).tests).forEach((testName) => {
      var _a2;
      (_a2 = this.persistantTestStorage) == null ? void 0 : _a2.set(testName, this.getTestVariant(testName));
    });
  }
};
_data = new WeakMap();

export { Abby };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.mjs.map